import { WalletContext, useWallet } from './wallet';
import { useEffect, useMemo, useState } from 'react';
import {
    addressOfByRegistry,
    createAGTickController,
    createERC20Controller,
    createLoanPoolController,
    createLoanStorageController,
    createAppTicket,
    createPaymentTokenERC20Controller,
    createPoolController,
    createStakePoolController,
    createStakePoolManagerController,
    createUniswapV2RouterController,
    getContractAddresses,
    isApproveToken,
    signWalletWrapper,
    createTokenRechargePool,
    createTokenManager,
    createIMToken,
    createUniswapV2FactoryController,
    balanceOf,
    createSwapPairController,
    createIMintToToken,
} from './contracts-controller';
import { IAGLoanPool, IAGLoanStorage, IAGPool, IAGStakePool, IAGStakePoolManager, IAGTick, IERC20, IAicadeRouter, IAppTicket, ITokenRechargePool, ITokenManager, IMToken, IMintTo } from './contracts/es/typescript/typechain';
import { BigNumber, ContractTransaction, BaseContract, ethers } from 'ethers';
import SandboxPoster from '@/framework/events/SandboxPoster';
import ContractConstants from './contract.constants';
import { useTranslation } from './i18n';
import { bigNumberMul, formatString, getDealine, waitSign } from '@/utils';
import { addTransaction } from '@/services/reducers/transactions';
import { useDispatch } from 'react-redux';
import { Token } from './token';
import { TotalAGDSupply } from '@/config';
import { loadTokenInfo, REDUCER_NAME as TOKEN_REDUCER_NAME, ContextTokens } from './reducers/token';
import { useAppDispatch, useAsyncEffect, useCombineSelector } from './hooks';

const slippageAmount = (amount: BigNumber, slippage: number) => {
    return amount.mul(BigNumber.from(100 - slippage)).div(BigNumber.from(100));
};

export const useGetContractAddress = (nameOrAddress: string): string => {
    const [address, setAddress] = useState('');
    const { accountChanged } = useWallet();
    useAsyncEffect(
        async () => {
            let tokenAddress;
            if (ethers.utils.isAddress(nameOrAddress)) {
                tokenAddress = await Promise.resolve(nameOrAddress);
            } else {
                tokenAddress = getContractAddresses([nameOrAddress])[0];
                if (!ethers.utils.isAddress(tokenAddress)) {
                    tokenAddress = await addressOfByRegistry(nameOrAddress);
                }
            }
            return tokenAddress;
        },
        setAddress,
        [nameOrAddress, accountChanged]
    );
    return address;
};

/**
 * 加载 token info
 * @param nameOrAddress
 * @returns
 */
export const useLoadTokenInfo = (nameOrAddress: string): Erc20TokenInfo | undefined => {
    const { chainName } = useWallet();
    const [counter] = useCombineSelector<TokenRecuderContext>(TOKEN_REDUCER_NAME, (state) => [state.counter]);
    const dispatch = useAppDispatch();
    const [tokenInfo, setTokenInfo] = useState(undefined as Erc20TokenInfo | undefined);
    useEffect(() => void dispatch(loadTokenInfo(nameOrAddress)), [chainName, nameOrAddress]);
    useEffect(() => {
        if (!chainName || !ContextTokens[chainName]) {
            setTokenInfo(undefined);
            return;
        }
        setTokenInfo(ContextTokens[chainName][nameOrAddress]);
    }, [counter, chainName, nameOrAddress]);
    return tokenInfo;
};

/**
 * 监听合约地址
 * @param name 合约注册名称
 * @returns 合约地址，可能为空
 */
export const useContractAddress = (names: string[]): string[] => {
    const { chainName } = useWallet();
    return useMemo(() => getContractAddresses(names), [chainName]);
};

/**
 * 创建一个合约对象
 * @param name 合约名称
 * @param contractCreator 合约创建期
 * @returns 合约对象
 */
export const useContractController = <T extends BaseContract>(name: string, contractCreator: ICreateController): T => {
    const { logged, accountChanged } = useWallet();
    const [contractAddress] = useContractAddress([name]);
    return useMemo(() => contractCreator(contractAddress), [logged, contractAddress, accountChanged]);
};

export const usePaymentTokenERC20Controller = (): UsePaymentTokenERC20Controller => {
    const { accountChanged } = useWallet();
    const { t: tc } = useTranslation('common', 'error');
    return useMemo(() => {
        return {
            async balanceOf(account: string) {
                const ercController = await createPaymentTokenERC20Controller();
                if (!ercController) {
                    throw new Error(tc('wallet-connect-error'));
                }
                return ercController.balanceOf(account);
            },
        };
    }, [accountChanged]);
};
/**
 * erc20 controller
 * @param name 代币名称
 * @returns 代币实例
 */
export const useERC20Controller = (name: 'AGD' | 'AGS'): UseERC20Controller => {
    const ercController = useContractController<IERC20>(name === 'AGS' ? ContractConstants.Names.AGSToken : ContractConstants.Names.AGDToken, createERC20Controller);
    const { t: tc } = useTranslation('common', 'error');
    return useMemo(() => {
        return {
            async balanceOf(account: string) {
                if (!ercController) {
                    throw new Error(tc('wallet-connect-error'));
                }
                return await ercController.balanceOf(account);
            },
            approveVault(amount: BigNumber = ethers.constants.MaxUint256): Promise<ContractTransaction> {
                const [vaultWallet] = getContractAddresses([ContractConstants.Names.VaultWallet]);
                return this.approve(vaultWallet, amount);
            },
            approve(spender: string, amount: BigNumber = ethers.constants.MaxUint256): Promise<ContractTransaction> {
                if (!ercController) {
                    throw new Error(tc('wallet-connect-error'));
                }
                return signWalletWrapper(() => waitSign(ercController.approve(spender, amount)));
            },
            async isApprove(spender: string, amount: BigNumber) {
                if (!ercController) {
                    throw new Error(tc('wallet-connect-error'));
                }
                return await isApproveToken(ercController.address, spender, amount);
            },
            async isApproveVault(amount: BigNumber) {
                const [vaultWallet] = getContractAddresses([ContractConstants.Names.VaultWallet]);
                return await this.isApprove(vaultWallet, amount);
            },
            async tokenInfo(): Promise<{ address: string; name: string; decimals: number; symbol: string; totalSupply: BigNumber }> {
                if (!ercController) {
                    throw new Error(tc('wallet-connect-error'));
                }
                return {
                    address: ercController.address,
                    name: await ercController.name(),
                    decimals: await ercController.decimals(),
                    symbol: await ercController.symbol(),
                    totalSupply: await ercController.totalSupply(),
                };
            },
        };
    }, [ercController]);
};
/**
 * AGD 铸造和销毁
 * @returns
 */
export const useAGPoolController = (): UseAGPoolController => {
    const agPoolController = useContractController<IAGPool>(ContractConstants.Names.AGPool, createPoolController);
    const { t: tc } = useTranslation('common', 'error');
    const { t: ts } = useTranslation('common', 'shade');
    const dispatch = useAppDispatch();
    return useMemo(() => {
        return {
            async mint(mintEther: number, onDone?: () => void): Promise<ContractTransaction> {
                return signWalletWrapper(async () => {
                    if (!agPoolController) {
                        throw new Error(tc('wallet-connect-error'));
                    }
                    const mintPrice: BigNumber = await agPoolController.currentMintPrice();
                    const needAmount = ethers.utils.parseUnits(mintEther.toString(), 'wei').mul(mintPrice);
                    const mintAmount = ethers.utils.parseUnits(`${mintEther}`, 'ether');
                    SandboxPoster.Post('Wallet.Mint', { token: mintEther, cToken: ethers.utils.formatUnits(needAmount, 'ether') });
                    // 开始mint
                    const pro = waitSign(agPoolController.mint(mintAmount));
                    dispatch(addTransaction({ data: formatString(ts('mint.all'), Token.AGD.name, mintEther, ethers.utils.formatUnits(needAmount, 'ether'), Token.PAYMENTTOKEN.name), type: 'Mint', transaction: pro, onDone }));
                    return pro;
                });
            },
            burn(amountEther: BigNumber, burnUsdtAmount: BigNumber, onDone?: () => void): Promise<ContractTransaction> {
                return signWalletWrapper(async () => {
                    if (!agPoolController) {
                        throw new Error(tc('wallet-connect-error'));
                    }
                    SandboxPoster.Post('Wallet.Burn', { token: ethers.utils.formatUnits(amountEther), cToken: ethers.utils.formatUnits(burnUsdtAmount) });
                    const burnAmount = ethers.utils.parseUnits(`${amountEther}`, 'wei');
                    // 开始mint
                    const pro = waitSign(agPoolController.burn(burnAmount));

                    dispatch(addTransaction({ data: formatString(ts('mint.all'), Token.AGD.name, ethers.utils.formatUnits(amountEther), ethers.utils.formatUnits(burnUsdtAmount), Token.PAYMENTTOKEN.name), type: 'Burn', transaction: pro, onDone }));
                    return pro;
                });
            },
            async evalBurnAmount(amountEther: BigNumber): Promise<BigNumber> {
                if (!agPoolController) {
                    throw new Error(tc('wallet-connect-error'));
                }
                const burnAmount = ethers.utils.parseUnits(`${amountEther}`, 'ether');
                return await agPoolController.evalBurnAmount(burnAmount);
            },
            async agTokenSummary(): Promise<AGTokenSummaryDatas> {
                if (!agPoolController) {
                    throw new Error(tc('wallet-connect-error'));
                }
                const [agTokenAddress] = getContractAddresses([ContractConstants.Names.AGDToken]);
                const agToken = createERC20Controller(agTokenAddress);
                if (!agToken) {
                    throw new Error(tc('wallet-connect-error'));
                }
                const totalHolderAmount = await agToken.totalSupply();
                const burnAmount = await agPoolController.totalBurnedAmount();
                return {
                    totalHolderAmount: totalHolderAmount,
                    totalBurnAmount: burnAmount,
                    totalSupplyAmount: TotalAGDSupply,
                };
            },
            async getCurMintPrice(): Promise<BigNumber> {
                if (!agPoolController) {
                    return BigNumber.from(0);
                }
                return await agPoolController.currentMintPrice();
            },
            async getAllDatas(): Promise<AGPoolData | undefined> {
                if (!agPoolController) {
                    return undefined;
                }
                return {
                    currentAGAmount: await agPoolController.currentAGAmount(),
                    currentMintPrice: await agPoolController.currentMintPrice(),
                    totalBurnedAmount: await agPoolController.totalBurnedAmount(),
                    minBurnAmount: await agPoolController.minBurnAmount(),
                    currentPaypableAmount: await agPoolController.currentPaypableAmount(),
                    currentMintNum: await agPoolController.currentMintNum(),
                };
            },
        };
    }, [agPoolController]);
};
/**
 * 借贷
 * @returns
 */
export const useLoanPool = (): UseLanPoolController => {
    const loanPool = useContractController<IAGLoanPool>(ContractConstants.Names.LoanPool, createLoanPoolController);
    const tickManager = useContractController<IAGTick>(ContractConstants.Names.LoanTick, createAGTickController);
    const loanStorage = useContractController<IAGLoanStorage>(ContractConstants.Names.LoanStorage, createLoanStorageController);
    const { t } = useTranslation('common', 'error');
    const { t: ts } = useTranslation('common', 'shade');
    const dispatch = useAppDispatch();
    return useMemo(() => {
        return {
            async loan(amountEther: BigNumber, gainPament: BigNumber, onDone?: () => void): Promise<ContractTransaction> {
                return signWalletWrapper(async () => {
                    if (!loanPool) {
                        throw new Error(t('wallet-connect-error'));
                    }
                    const amount = ethers.utils.parseUnits(`${amountEther}`, 'ether');
                    const pro = waitSign(loanPool.loan(amount));
                    dispatch(addTransaction({ transaction: pro, onDone, type: 'Loan', data: formatString(ts('loan.all'), Token.AGD.name, amountEther, ethers.utils.formatUnits(gainPament), Token.PAYMENTTOKEN.name) }));
                    return pro;
                });
            },
            async getTick(tickId: number): Promise<LoanTickInfo> {
                const loanTickInfo = await loanStorage.getTick(tickId);
                return { ...loanTickInfo, tickId: tickId, returnLoading: false };
            },
            async balanceOf(): Promise<number> {
                return (await tickManager.balanceOf(WalletContext.Address)).toNumber();
            },
            async ownerOf(tickId: number): Promise<boolean> {
                return (await tickManager.ownerOf(tickId)) == WalletContext.Address;
            },
            async ownerOfTokens(): Promise<LoanTickInfo[]> {
                if (!loanPool || !tickManager) {
                    throw new Error(t('wallet-connect-error'));
                }
                const num = (await tickManager.balanceOf(WalletContext.Address)).toNumber();
                if (num == 0) {
                    return [];
                }
                const ticks = [] as LoanTickInfo[];
                for (let i = num - 1; i >= 0; i--) {
                    const tickId = await tickManager.tokenOfOwnerByIndex(WalletContext.Address, i);
                    const loanTickInfo = await loanStorage.getTick(tickId);
                    ticks.push({ ...loanTickInfo, tickId: tickId.toNumber(), returnLoading: false });
                }
                return ticks;
            },
            async ownerOfTokensPages(start: number, end: number): Promise<LoanTickInfo[]> {
                if (!loanPool || !tickManager) {
                    throw new Error(t('wallet-connect-error'));
                }
                const num = end;
                const ticks = [] as LoanTickInfo[];
                for (let i = num - 1; i >= start; i--) {
                    const tickId = await tickManager.tokenOfOwnerByIndex(WalletContext.Address, i);
                    const loanTickInfo = await loanStorage.getTick(tickId);
                    ticks.push({ ...loanTickInfo, tickId: tickId.toNumber(), returnLoading: false });
                }
                return ticks;
            },
            async unlan(tickId: number, onDone?: () => void): Promise<ContractTransaction> {
                return signWalletWrapper(async () => {
                    if (!loanPool || !tickManager) {
                        throw new Error(t('wallet-connect-error'));
                    }
                    // 获取需要归还的金额
                    const tick = await loanStorage.getTick(tickId);
                    if (!tick || tick.unloanAmount.lte(BigNumber.from(0))) {
                        throw new Error('tick error.');
                    }
                    // 开始借贷
                    const pro = waitSign(loanPool.unloan(tickId));
                    dispatch(addTransaction({ transaction: pro, onDone, type: 'Unloan', data: formatString(ts('loan.all'), Token.AGD.name, ethers.utils.formatUnits(tick.amount), ethers.utils.formatUnits(tick.unloanAmount), Token.PAYMENTTOKEN.name) }));
                    return pro;
                });
            },
            async getAllDatas(): Promise<{ serviceFee: number; fee30Days: number }> {
                if (!loanPool || !tickManager) {
                    throw new Error(t('wallet-connect-error'));
                }
                return await loanPool.loanFee();
            },
        };
    }, [loanPool, tickManager, loanStorage]);
};

export const useStakePoolController = (): UseStakePoolController => {
    const stakePool = useContractController<IAGStakePool>(ContractConstants.Names.StakePool, createStakePoolController);
    const tickManager = useContractController<IAGTick>(ContractConstants.Names.StakeTick, createAGTickController);
    const stakeStorage = useContractController<IAGStakePoolManager>(ContractConstants.Names.LoanStorage, createStakePoolManagerController);
    const { t } = useTranslation('common', 'error');
    return useMemo(() => {
        return {
            staking(amountEther: BigNumber, days: number): Promise<ContractTransaction> {
                if (!stakePool) {
                    throw new Error(t('wallet-connect-error'));
                }
                return signWalletWrapper(() => waitSign(stakePool.staking(amountEther, days)));
            },
            unstaking(tickId: number): Promise<ContractTransaction> {
                if (!stakePool) {
                    throw new Error(t('wallet-connect-error'));
                }
                return signWalletWrapper(() => waitSign(stakePool.unstaking(tickId)));
            },
            harvest(tickId: number, revenueId: number, token: string): Promise<ContractTransaction> {
                if (!stakePool) {
                    throw new Error(t('wallet-connect-error'));
                }
                return signWalletWrapper(() => waitSign(stakePool.harvest(tickId, revenueId, token)));
            },
            async revenueInfo(revenueId: number) {
                if (!stakePool) {
                    throw new Error(t('wallet-connect-error'));
                }
                return await stakePool.revenueInfo(revenueId);
            },
            async revenueTokens(tickId: number, revenueId: number) {
                if (!stakePool) {
                    throw new Error(t('wallet-connect-error'));
                }
                return await stakePool.revenueTokens(tickId, revenueId);
            },
            async revenueTokenAddresses() {
                if (!stakePool) {
                    throw new Error(t('wallet-connect-error'));
                }
                return await stakePool.revenueTokenAddresses();
            },
            async stakeDays(stakeDays: number) {
                if (!stakePool) {
                    throw new Error(t('wallet-connect-error'));
                }
                return await stakePool.stakeDays(stakeDays);
            },
        };
    }, [stakePool, tickManager, stakeStorage]);
};
export const useSwapTokenController = (): UseSwapTokenController => {
    const uniswapV2Router = useContractController<IAicadeRouter>(ContractConstants.Names.SwapRouter, createUniswapV2RouterController);
    const [factoryAddress] = getContractAddresses([ContractConstants.Names.SwapFactory]);
    const dispatch = useAppDispatch();
    const { t } = useTranslation('common', 'error');
    const { t: ts } = useTranslation('common', 'shade');
    const { t: tt } = useTranslation('common', 'transactions');
    return useMemo(() => {
        const controller: UseSwapTokenController = {
            async getPair(tokenA: string, tokenB: string) {
                const uniFactory = createUniswapV2FactoryController(factoryAddress);
                if (!uniswapV2Router || !uniFactory) {
                    throw new Error(t('wallet-connect-error'));
                }
                return await uniFactory.getPair(tokenA, tokenB);
            },
            async getPairFee(pairAddress: string) {
                const uniFactory = createUniswapV2FactoryController(factoryAddress);
                if (!uniswapV2Router || !uniFactory) {
                    throw new Error(t('wallet-connect-error'));
                }
                return await uniFactory.pairFee(pairAddress);
            },
            async addLiquidity(tokenA: string, tokenB: string, amountA: BigNumber, amountB: BigNumber, fee: number, slippage = 3) {
                return signWalletWrapper(async () => {
                    if (!uniswapV2Router) {
                        throw new Error(t('wallet-connect-error'));
                    }
                    const addLiquidityData = {
                        tokenA: tokenA,
                        tokenB: tokenB,
                        tokenANumber: ethers.utils.formatUnits(amountA),
                        tokenBNumber: ethers.utils.formatUnits(amountB),
                        fee: fee,
                    };
                    SandboxPoster.Post('AddLiquidity', addLiquidityData);
                    const pro = await uniswapV2Router.addLiquidity(tokenA, tokenB, amountA, amountB, slippageAmount(amountA, slippage), slippageAmount(amountB, slippage), getDealine(60));
                    dispatch(addTransaction({ data: tt('actions.add-liquidity'), type: 'Mint', transaction: pro }));
                    return pro;
                });
            },
            async createSwapPair(tokenA: string, tokenB: string, amountA: BigNumber, amountB: BigNumber, fee: number, onlyAdminAddLiquidity: boolean) {
                const uniFactory = createUniswapV2FactoryController(factoryAddress);
                if (!uniswapV2Router || !uniFactory) {
                    throw new Error(t('wallet-connect-error'));
                }
                const addLiquidityData = {
                    tokenA: tokenA,
                    tokenB: tokenB,
                    tokenANumber: ethers.utils.formatUnits(amountA),
                    tokenBNumber: ethers.utils.formatUnits(amountB),
                    fee: fee,
                };
                SandboxPoster.Post('AddLiquidity', addLiquidityData);
                try {
                    const ab = await uniswapV2Router.createPair(tokenA, tokenB, amountA, amountB, fee, onlyAdminAddLiquidity, getDealine(60));
                    await ab.wait();
                    dispatch(addTransaction({ data: tt('actions.add-liquidity'), type: 'Mint', transaction: ab }));
                    SandboxPoster.Post('Wallet.SignSuccess', {});
                    return await uniFactory.getPair(tokenA, tokenB);
                } catch (e) {
                    SandboxPoster.Post('Wallet.SignFail');
                    return '';
                }
            },
            async getRemoveLiquidityAmounts(tokenA: string, tokenB: string, liquidAmount: BigNumber) {
                const uniFactory = createUniswapV2FactoryController(factoryAddress);
                if (!uniswapV2Router || !uniFactory) {
                    throw new Error(t('wallet-connect-error'));
                }
                const pairAddress = await uniFactory.getPair(tokenA, tokenB);
                if (pairAddress == ethers.constants.AddressZero) {
                    return { amount0: ethers.constants.Zero, amount1: ethers.constants.Zero };
                }
                const pair = createSwapPairController(pairAddress);
                if (!pair) {
                    throw new Error(t('wallet-connect-error'));
                }
                return await pair.getBurnAmount(tokenA, tokenB, liquidAmount);
            },
            async removeLiquidity(tokenA: string, tokenB: string, liquidAmount: BigNumber, slippage = 3) {
                return signWalletWrapper(async () => {
                    const uniFactory = createUniswapV2FactoryController(factoryAddress);
                    if (!uniswapV2Router || !uniFactory) {
                        throw new Error(t('wallet-connect-error'));
                    }
                    const pairAddress = await uniFactory.getPair(tokenA, tokenB);
                    const pair = createSwapPairController(pairAddress);
                    if (!pair) {
                        throw new Error(t('wallet-connect-error'));
                    }
                    const { amount0, amount1 } = await pair.getBurnAmount(tokenA, tokenB, liquidAmount);
                    const addLiquidityData = {
                        tokenA: tokenA,
                        tokenB: tokenB,
                        tokenANumber: ethers.utils.formatUnits(liquidAmount),
                    };
                    SandboxPoster.Post('RemoveLiquidity', addLiquidityData);
                    const ab = await uniswapV2Router.removeLiquidity(tokenA, tokenB, liquidAmount, slippageAmount(amount0, slippage), slippageAmount(amount1, slippage), getDealine(60));
                    await ab.wait();
                    dispatch(addTransaction({ data: tt('actions.remove-liquidity'), type: 'Mint', transaction: ab }));
                    return ab;
                });
            },
            async evalLiquidityValut(_tokenA: string, _tokenB: string, liquidAmount: BigNumber) {
                if (!uniswapV2Router) {
                    throw new Error(t('wallet-connect-error'));
                }
                let tokenA = _tokenA;
                let tokenB = _tokenB;
                // 获取 AGD 地址
                const [agdAddress] = getContractAddresses([ContractConstants.Names.AGDToken]);
                if (tokenA == agdAddress) {
                    tokenA = _tokenB;
                    tokenB = _tokenA;
                } else if (tokenB != agdAddress) {
                    throw new Error('AGD Address ERROR');
                }
                // 查询流动性可退价值
                const amounts = await controller.getRemoveLiquidityAmounts(tokenA, tokenB, liquidAmount);
                // 获取 price
                const price = await controller.getPrice(tokenA, tokenB);
                // 计算价值
                return bigNumberMul(amounts.amount0, price).add(amounts.amount1);
            },
            async getPrice(tokenIn: string, tokenOut: string) {
                if (!uniswapV2Router) {
                    throw new Error(t('wallet-connect-error'));
                }
                return await uniswapV2Router.quote(ethers.utils.parseUnits('1', 'ether'), tokenIn, tokenOut);
            },
            async getAmountOut(tokenIn: string, tokenOut: string, amountInEther: string) {
                if (!uniswapV2Router) {
                    throw new Error(t('wallet-connect-error'));
                }
                if (parseFloat(amountInEther) <= 0) {
                    throw new Error('amount in is error');
                }
                return await uniswapV2Router.getAmountOut(tokenIn, tokenOut, ethers.utils.parseUnits(`${amountInEther}`, 'ether'));
            },
            async getCurLiquidity(tokenIn, tokenOut) {
                const factory = createUniswapV2FactoryController(factoryAddress);
                if (!uniswapV2Router || !WalletContext.Address || !factory) {
                    throw new Error(t('wallet-connect-error'));
                }
                const pairAddress = await factory.getPair(tokenIn, tokenOut);
                if (pairAddress == ethers.constants.AddressZero) {
                    return ethers.constants.Zero;
                }
                return balanceOf(pairAddress);
            },
            async getAllLiquidity(tokenIn, tokenOut) {
                const factory = createUniswapV2FactoryController(factoryAddress);
                if (!uniswapV2Router || !WalletContext.Address || !factory) {
                    throw new Error(t('wallet-connect-error'));
                }
                const pairAddress = await factory.getPair(tokenIn, tokenOut);
                if (pairAddress == ethers.constants.AddressZero) {
                    return ethers.constants.Zero;
                }
                const erc20 = await createERC20Controller(pairAddress);
                if (!erc20) {
                    throw new Error(t('wallet-connect-error'));
                }
                return await erc20.totalSupply();
            },
            async swap(tokenIn, tokenOut, amountInEther, swapTokenInfo, slippage = 3) {
                if (!uniswapV2Router || !WalletContext.Address) {
                    throw new Error(t('wallet-connect-error'));
                }
                const tokenAmountIn = ethers.utils.parseUnits(`${amountInEther}`, 'ether');
                const amountOut = await uniswapV2Router.getAmountOut(tokenIn, tokenOut, tokenAmountIn);
                const swapData = {
                    token: amountInEther.toString(),
                    tokenSymbol: swapTokenInfo.tokenInSymbol,
                    cToken: Number(ethers.utils.formatUnits(amountOut)).toFixed(3).toString(),
                    cTokenSymbol: swapTokenInfo.tokenOutSymbol,
                    tokenIcon: swapTokenInfo.tokenInIcon,
                    cTokenIcon: swapTokenInfo.tokenOutIcon,
                };
                SandboxPoster.Post('Wallet.Swapping', swapData);
                // 计算最小滑点
                const amountOutMin = slippageAmount(amountOut, slippage);
                const pro = signWalletWrapper(() => waitSign(uniswapV2Router.swapExactTokensForTokens(tokenAmountIn, amountOutMin, [tokenIn, tokenOut], getDealine(60))));
                dispatch(addTransaction({ transaction: pro, type: 'Swap', data: formatString(ts('swap.all'), swapData.token, swapData.tokenSymbol, swapData.cToken, swapData.cTokenSymbol) }));
                return pro;
            },
        };
        return controller;
    }, [uniswapV2Router]);
};

export const useAppTicketController = (): UseAppTicketController => {
    const appTicket = useContractController<IAppTicket>(ContractConstants.Names.AppTicket, createAppTicket);
    const { t } = useTranslation('common', 'error');
    const { t: tt } = useTranslation('common', 'transactions');
    const dispatch = useAppDispatch();
    const { address, accountChanged } = useWallet();
    return useMemo(() => {
        return {
            async payTicket(dappNo: string, payToken: string, payAmount: BigNumber, ticketId: string, signature: string) {
                if (!appTicket) {
                    throw new Error(t('wallet-connect-error'));
                }
                SandboxPoster.Post('Wallet.Ticket', { tokenSymbol: payToken, token: ethers.utils.formatUnits(payAmount, 'ether') });
                const pro = signWalletWrapper(() => waitSign(appTicket.payTicket(dappNo, payToken, payAmount, ticketId, signature)));
                dispatch(addTransaction({ data: tt('actions.pay-ticket'), type: 'Ticket', transaction: pro }));
                //监听交易成功
                return pro;
            },

            async subscribe(dappNo: string, payToken: string, payAmount: BigNumber, signature: string) {
                SandboxPoster.Post('Wallet.Ticket', { tokenSymbol: payToken, token: ethers.utils.formatUnits(payAmount, 'ether') });
                const pro = signWalletWrapper(() => waitSign(appTicket.subscribe(dappNo, payToken, payAmount, signature)));
                dispatch(addTransaction({ data: tt('actions.subscribe-dapp'), type: 'Ticket', transaction: pro }));
                //监听交易成功
                return pro;
            },

            async isPayedTicket(dappNo: string, ticketNo: string) {
                if (!appTicket) {
                    throw new Error(t('wallet-connect-error'));
                }
                return await appTicket.isPayedTicket(dappNo, address, ticketNo);
            },
            async isSubscribe(dappNo: string) {
                if (!appTicket) {
                    throw new Error(t('wallet-connect-error'));
                }
                return await appTicket.isSubscribe(dappNo, address);
            },
        };
    }, [appTicket, t, tt, accountChanged]);
};

export const useTokenRechargeController = (): UseTokenRechargeController => {
    const { t } = useTranslation('common', 'error');
    const { t: tt } = useTranslation('common', 'transactions');
    const dispatch = useAppDispatch();
    const tokenRecharge = useContractController<ITokenRechargePool>(ContractConstants.Names.TokenRechargePool, createTokenRechargePool);
    return useMemo(() => {
        return {
            async deposit(amount: BigNumber, onDone?: () => void) {
                if (!tokenRecharge) {
                    throw new Error(t('wallet-connect-error'));
                }

                const [agsToken] = getContractAddresses([ContractConstants.Names.AGSToken]);
                const depositAmount = ethers.utils.parseUnits(`${amount}`, 'ether');
                console.log('deposit is %o,%o,%o,%o', amount, depositAmount.toString(), agsToken, tokenRecharge.address);
                const pro = signWalletWrapper(() => waitSign(tokenRecharge.deposit(agsToken, depositAmount)));
                dispatch(addTransaction({ data: formatString(tt('actions.deposit-ags'), amount), onDone, type: 'Deposit', transaction: pro }));
                return pro;
            },
            async withdrawal(amount: BigNumber, orderId: BigNumber, signature: string, onDone?: () => void) {
                if (!tokenRecharge) {
                    throw new Error(t('wallet-connect-error'));
                }
                const [agsToken] = getContractAddresses([ContractConstants.Names.AGSToken]);
                const withdrawalAmount = ethers.utils.parseUnits(`${amount}`, 'ether');
                const pro = signWalletWrapper(() => waitSign(tokenRecharge.withdrawal(agsToken, withdrawalAmount, orderId, signature)));
                dispatch(addTransaction({ data: tt('actions.token-recharge'), onDone, type: 'Withdrawal', transaction: pro }));
                return pro;
            },
            async withdrawOrderState(orderId: BigNumber) {
                if (!tokenRecharge) {
                    throw new Error(t('wallet-connect-error'));
                }
                return await tokenRecharge.withdrawOrderState(orderId);
            },
        };
    }, [tokenRecharge, t, tt]);
};

export const useTokenMananger = (): UseTokenManagerController => {
    const { t } = useTranslation('common', 'error');
    const { t: tt } = useTranslation('common', 'transactions');
    const { accountChanged } = useWallet();
    const tokenMananger = useContractController<ITokenManager>(ContractConstants.Names.TokenManager, createTokenManager);
    const dispatch = useAppDispatch();
    return useMemo(() => {
        return {
            async createToken(name: string, symbol: string) {
                if (!tokenMananger) {
                    throw new Error(t('wallet-connect-error'));
                }
                SandboxPoster.Post('Token.Create', 'Create Token symbol ' + symbol);
                const pro = await waitSign(tokenMananger.createToken(name, symbol));
                pro.wait();
                dispatch(addTransaction({ data: tt('actions.create-token'), type: 'Mint', transaction: pro }));
                SandboxPoster.Post('Wallet.SignSuccess', {});
                return pro;
            },
            async mint(tokenAddr: string, amountEther: string) {
                const token = createIMintToToken(tokenAddr) as IMintTo;
                SandboxPoster.Post('Token.Mint', { token: amountEther, tokenAddress: tokenAddr });
                // 开始mint
                const pro = await waitSign(token.mintTo(ethers.utils.parseUnits(amountEther, 'ether'), WalletContext.Address));
                pro.wait();
                dispatch(addTransaction({ data: tt('actions.mint-token'), type: 'Mint', transaction: pro }));
                SandboxPoster.Post('Wallet.SignSuccess', {});
            },
            async createCappedToken(name: string, symbol: string, maxTotalSupply: number) {
                if (!tokenMananger) {
                    throw new Error(t('wallet-connect-error'));
                }
                SandboxPoster.Post('Token.Create', 'Create Token symbol ' + symbol);
                const pro = await waitSign(tokenMananger.createCappedToken(name, symbol, ethers.utils.parseUnits(`${maxTotalSupply}`, 'ether')));
                pro.wait();
                dispatch(addTransaction({ data: tt('actions.create-token'), type: 'Mint', transaction: pro }));
                SandboxPoster.Post('Wallet.SignSuccess', {});
                return pro;
            },
            async changeOwner(addr: string, newOwner: string) {
                SandboxPoster.Post('Msg', 'You Change Token Owner is Address ' + newOwner);
                const pro = await waitSign((createIMToken(addr) as IMToken).transferOwnership(newOwner));
                pro.wait();
                dispatch(addTransaction({ data: tt('actions.change-token-owner'), type: 'Mint', transaction: pro }));
                SandboxPoster.Post('Wallet.SignSuccess', {});
                return pro;
            },
            async containsToken(tokenAddr: string) {
                return tokenMananger.containsToken(tokenAddr);
            },
        };
    }, [accountChanged, t, tt, tokenMananger]);
};
